import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import {
  isNotReportingReasonComplete,
  NotReportingReasonInput,
} from "components/Submission/NotReportingReasonInput";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionNotReportingReason, SubmissionStatus } from "generated-graphql/graphql";
import pluralize from "pluralize";
import {
  BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY,
  BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION,
} from "queries/submissions";
import { useEffect, useMemo, useState } from "react";
import { formatDateLikeForDisplay } from "util/dates";
import { STATUS_LABELS } from "utils/submissionStatusLabels";

import type { Row } from "./types";

import { formatVirtualEventForGrid, partitionSubmissions } from "./utils";

interface ChangeStatusDialogProps {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess?: () => void;
}

export function ChangeStatusDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: ChangeStatusDialogProps) {
  const alerts = useAlerts();
  const [selectedStatus, setSelectedStatus] = useState<SubmissionStatus | "">("");
  const [notReportingReason, setNotReportingReason] = useState<SubmissionNotReportingReason | "">(
    ""
  );
  const [notReportingReasonNote, setNotReportingReasonNote] = useState("");

  // Partition virtual events from real submissions
  const { virtualEvents, uniqueSubmissionIds, virtualEventIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );
  const submissionCount = uniqueSubmissionIds.length + virtualEventIds.length;

  const [checkSafety, { data: safetyData, loading: checkingLoading, error: safetyError }] =
    useLazyQuery(BULK_SUBMISSIONS_SAFE_TO_UPDATE_QUERY, {
      fetchPolicy: "network-only",
    });

  const [bulkUpdateSubmissionsStatus, { loading: mutationLoading }] = useMutation(
    BULK_UPDATE_SUBMISSIONS_STATUS_MUTATION,
    {
      onCompleted: (data) => {
        if (data.bulkUpdateSubmissionsStatus.succeeded) {
          alerts.success(
            <>
              Job queued to update submissions.{" "}
              <Link href={`/staff/jobs/${data.bulkUpdateSubmissionsStatus.jobId}`} target="_blank">
                View job
              </Link>
            </>
          );
          handleClose();
          onSuccess?.();
        }
      },
      onError: (error) => {
        alerts.error(`Failed to queue submission update job: ${error.message}`);
      },
      refetchQueries: ["SubmissionVirtualEvents", "GetSubmission"],
    }
  );

  const loading = checkingLoading || mutationLoading;

  useEffect(() => {
    if (open && selectedStatus && uniqueSubmissionIds.length > 0) {
      checkSafety({
        variables: {
          submissionIds: uniqueSubmissionIds,
          targetStatus: selectedStatus,
        },
      });
    }
  }, [open, selectedStatus, uniqueSubmissionIds, checkSafety]);

  const handleClose = () => {
    setSelectedStatus("");
    setNotReportingReason("");
    setNotReportingReasonNote("");
    onClose();
  };

  const handleStatusChange = (event: SelectChangeEvent<SubmissionStatus>) => {
    setSelectedStatus(event.target.value);
    setNotReportingReason("");
    setNotReportingReasonNote("");
  };

  const VIRTUAL_EVENT_ALLOWED_STATUSES: SubmissionStatus[] = [
    SubmissionStatus.InProgress,
    SubmissionStatus.NotReporting,
    SubmissionStatus.NotStarted,
  ];

  const canMaterializeVirtualEvents =
    !!selectedStatus && VIRTUAL_EVENT_ALLOWED_STATUSES.includes(selectedStatus);

  const { safeSubmissions, unsafeSubmissions, virtualEventRows } = useMemo(() => {
    const safe = safetyData?.bulkSubmissionsSafeToUpdate
      ? safetyData.bulkSubmissionsSafeToUpdate.filter((item: { isSafe: boolean }) => item.isSafe)
      : [];
    const unsafe = safetyData?.bulkSubmissionsSafeToUpdate
      ? safetyData.bulkSubmissionsSafeToUpdate.filter((item: { isSafe: boolean }) => !item.isSafe)
      : [];

    const virtualRows = canMaterializeVirtualEvents
      ? virtualEvents.map(formatVirtualEventForGrid)
      : [];

    return {
      safeSubmissions: safe,
      unsafeSubmissions: unsafe,
      virtualEventRows: virtualRows,
    };
  }, [safetyData, virtualEvents, canMaterializeVirtualEvents]);

  const isNotReporting = selectedStatus === SubmissionStatus.NotReporting;
  const reasonComplete =
    !isNotReporting || isNotReportingReasonComplete(notReportingReason, notReportingReasonNote);
  const canSubmit =
    !!selectedStatus &&
    reasonComplete &&
    (safeSubmissions.length > 0 || virtualEventRows.length > 0);
  const updateCount = safeSubmissions.length + virtualEventRows.length;
  const convertedSubmissionDescription =
    virtualEventIds.length === 1 ? "a new submission" : `${virtualEventIds.length} new submissions`;

  const handleSubmit = async () => {
    if (!selectedStatus || (safeSubmissions.length === 0 && virtualEventRows.length === 0)) {
      return;
    }

    let notReportingInput = {};
    if (isNotReporting && notReportingReason) {
      const trimmedNote = notReportingReasonNote.trim();

      notReportingInput = {
        notReportingReason,
        notReportingReasonNote: trimmedNote || undefined,
      };
    }

    await bulkUpdateSubmissionsStatus({
      variables: {
        input: {
          submissionIds: safeSubmissions.map((s: { id: string }) => s.id),
          virtualEventIds: canMaterializeVirtualEvents ? virtualEventIds : [],
          status: selectedStatus,
          ...notReportingInput,
        },
      },
    });
  };

  const allowedStatuses = [
    {
      value: SubmissionStatus.InProgress,
      label: STATUS_LABELS[SubmissionStatus.InProgress],
    },
    {
      value: SubmissionStatus.EncampInitialReview,
      label: STATUS_LABELS[SubmissionStatus.EncampInitialReview],
    },
    {
      value: SubmissionStatus.InReview,
      label: STATUS_LABELS[SubmissionStatus.InReview],
    },
    {
      value: SubmissionStatus.ProcessingSubmission,
      label: STATUS_LABELS[SubmissionStatus.ProcessingSubmission],
    },
    {
      value: SubmissionStatus.FilingComplete,
      label: STATUS_LABELS[SubmissionStatus.FilingComplete],
    },
    {
      value: SubmissionStatus.NotReporting,
      label: STATUS_LABELS[SubmissionStatus.NotReporting],
    },
  ];

  const columns: CustomGridColDef<any>[] = [
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 100,
      valueGetter: (value, row) =>
        row.submission.dueDate ? formatDateLikeForDisplay(row.submission.dueDate) : "",
    },
    {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      minWidth: 120,
      valueGetter: (value, row) => {
        // For virtual events
        if (row.isVirtual) {
          return row.facilities?.map((f: { name: string }) => f.name).join(", ") || "";
        }
        // For real submissions
        return (
          row.submission.facilitySubmissions
            ?.map((fs: { facility: { name: string } }) => fs.facility.name)
            .join(", ") || ""
        );
      },
    },
    {
      field: "submissionSummary",
      headerName: "Submission",
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => {
        if (row.submission.title) {
          return row.submission.title;
        }
        return prettyPrintEnumValue(row.submission.submissionType);
      },
    },
    {
      field: "currentStatus",
      headerName: "Current Status",
      width: 130,
      valueGetter: (value, row) =>
        row.isVirtual ? "Virtual Event" : STATUS_LABELS[row.submission.status],
    },
  ];

  const blockedColumns: CustomGridColDef<(typeof unsafeSubmissions)[number]>[] = [
    ...columns.slice(0, 3),
    {
      field: "reason",
      headerName: "Reason Blocked",
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) => row.reasons.join("; "),
    },
  ];

  return (
    <ConfirmDialog
      open={open}
      onClose={handleClose}
      onConfirm={canSubmit ? handleSubmit : undefined}
      header={`Change Status for ${submissionCount} ${pluralize("Submission", submissionCount)}`}
      loading={loading}
      disableConfirm={!canSubmit}
      confirmText="Confirm"
      minWidth="min(640px, calc(100vw - 64px))"
      maxWidth="min(640px, calc(100vw - 64px))"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Are you sure you want to update the status of the selected {submissionCount}{" "}
          {pluralize("submission", submissionCount)}?
          {virtualEventIds.length > 0 && (
            <>
              <br />
              <strong>Note:</strong> {virtualEventIds.length}{" "}
              {pluralize("virtual event", virtualEventIds.length)} will be converted to{" "}
              {convertedSubmissionDescription} with the selected status.
            </>
          )}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            columnGap: 2,
            rowGap: 3,
          }}
        >
          <FormControl fullWidth required>
            <InputLabel id="status-select-label">Target Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={selectedStatus}
              label="Target Status"
              onChange={handleStatusChange}
              disabled={loading}
            >
              {allowedStatuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isNotReporting && (
            <NotReportingReasonInput
              reason={notReportingReason}
              note={notReportingReasonNote}
              onReasonChange={setNotReportingReason}
              onNoteChange={setNotReportingReasonNote}
              disabled={loading}
            />
          )}
        </Box>

        {selectedStatus && checkingLoading && uniqueSubmissionIds.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Checking which submissions can be updated...
            </Typography>
          </Box>
        )}

        {selectedStatus && safetyError && (
          <Alert severity="error">Failed to check submission status: {safetyError.message}</Alert>
        )}

        {selectedStatus && !canMaterializeVirtualEvents && virtualEventIds.length > 0 && (
          <Alert severity="info">
            {virtualEventIds.length} {pluralize("virtual event", virtualEventIds.length)} will be
            skipped &mdash; only existing submissions can be moved to this status.
          </Alert>
        )}

        {selectedStatus &&
          !checkingLoading &&
          (uniqueSubmissionIds.length > 0 ? safetyData : true) && (
            <Box>
              {(safeSubmissions.length > 0 || virtualEventRows.length > 0) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {pluralize("Submission", updateCount)} to Update ({updateCount})
                    {virtualEventRows.length > 0 && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({virtualEventRows.length} new{" "}
                        {pluralize("submission", virtualEventRows.length)} will be created)
                      </Typography>
                    )}
                  </Typography>
                  <DataGrid
                    rows={[...safeSubmissions, ...virtualEventRows]}
                    columns={columns}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[5, 10]}
                    hideFooterPagination={updateCount < 10}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    sx={{ maxHeight: 600 }}
                  />
                </Box>
              )}

              {unsafeSubmissions.length > 0 && (
                <Box>
                  <Alert
                    severity={
                      safeSubmissions.length === 0 && virtualEventRows.length === 0
                        ? "error"
                        : "warning"
                    }
                    sx={{ mb: 1 }}
                  >
                    {safeSubmissions.length === 0 && virtualEventRows.length === 0
                      ? "None of the selected submissions can be updated. See details below."
                      : `${unsafeSubmissions.length} ${pluralize("submission", unsafeSubmissions.length)} cannot be updated and will be skipped`}
                  </Alert>
                  <DataGrid
                    rows={unsafeSubmissions}
                    columns={blockedColumns}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="client"
                    pageSizeOptions={[5, 10]}
                    hideFooterPagination={unsafeSubmissions.length < 10}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    sx={{ maxHeight: 600 }}
                  />
                </Box>
              )}
            </Box>
          )}
      </Stack>
    </ConfirmDialog>
  );
}
